// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal layout from the design system)
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, CalendarDays, CalendarPlus, CheckCircle2, ChevronDown, ClipboardList, Eye, MapPin,
  SlidersHorizontal, Sparkles, Star, Store as StoreIcon, UserMinus, UserPlus, Users, X, Download, ListChecks,
} from 'lucide-react';
import EmployeeTags from '../EmployeeTags';
import { onboardingAPI } from '../../services/api';
import { buildCsv, downloadCsv } from '../../utils/csv';
import { Modal, SearchBox } from './DashboardPrimitives';
import DashboardFilterModal, { type FilterOption, type FilterSection } from './DashboardFilterModal';
import { InlineLoader } from '../loading';
import {
  NO_BUDDY_LIST, ONBOARDING_SCORE_GROUPS, isDefaultOnboardingView, onboardingSlug,
  onboardingViewFromSlug, type OnboardingPlan, type OnboardingScoreGroup, type OnboardingView,
} from './lists/onboarding';
import { useDashboardList } from './useDashboardList';
import { useLinkedSearch } from '../../hooks/useLinkedSearch';
import { useTableSort, SortTh } from './useTableSort';
import OnboardingScoreModal from './OnboardingScoreModal';
import OnboardingAnswersView, { FORM_LABEL, type OnboardingForm } from './OnboardingAnswersView';
import {
  ADVICE_META, TONE_PILL, formatScore, istDay, scoreCaption, scorePill, shortDate,
  type JoinerScore, type ScoreDecision,
} from './onboardingScore';

/**
 * Dashboard → Onboarding.
 *
 * Lists everyone who joined in the last few days and lets a coordinator pair each
 * of them with a buddy for a shadow training week.
 *
 * The thing to understand before reading further: saving a pairing DEPLOYS the
 * joiner to the buddy's store for that period. That is not incidental — a new
 * joiner with no store cannot log in to the mobile app at all, so the placement
 * is what makes them able to turn up and mark attendance. The nightly sweep
 * removes the placement once the window closes, and only if the joiner is still
 * at that store, so a permanent posting made mid-week is never disturbed.
 */

// ── Types (mirror onboarding.service) ────────────────────────────────────────

interface ShadowAssignment {
  id: number;
  employeeCode: string;
  employeeName: string | null;
  buddyCode: string;
  buddyName: string | null;
  storeId: string;
  storeName: string | null;
  startDate: string;
  endDate: string;
  /** The joiner's actual shadow days, possibly with gaps; null on legacy rows
   *  (those shadow every day from startDate to endDate). */
  shadowDates?: string[] | null;
  status: string;
  deployed: boolean;
  /** 'auto' = placed by the city auto-assign engine; 'manual' = a coordinator did. */
  assignmentSource?: 'manual' | 'auto';
  // Present on the list endpoint, which joins `stores`; absent on the row
  // returned straight after a save.
  storeCode?: string | null;
  storeAddress?: string | null;
  storeCity?: string | null;
  storeState?: string | null;
  storePinCode?: string | null;
  storeTrade?: string | null;
  storeCategory?: string | null;
  /** The joiner's own area + joining date, enriched server-side from 1Office. */
  joinerCity?: string | null;
  joinerState?: string | null;
  joinerDateOfJoining?: string | null;
}

interface NewJoiner {
  employeeCode: string;
  employeeName: string;
  designation: string;
  city: string;
  state: string;
  dateOfJoining: string | null;
  daysSinceJoining: number | null;
  isDeployed: boolean;
  currentStoreName: string | null;
  assignment: ShadowAssignment | null;
  /** The most recent PAST (completed/cancelled) assignment — the buddy + dates a
   *  completed plan's modal shows read-only. Null if they never had one. */
  lastAssignment: ShadowAssignment | null;
  /** A Buddy-Pool member is deployed in this joiner's city → auto-assign eligible. */
  hasCityBuddyMatch: boolean;
  /** True once the joiner's onboarding plan has been initiated as a batch. */
  batchInitiated: boolean;
  /** What the plan schedules for the joiner TODAY (their current journey day +
   *  whether it's a shadow day or which sessions run). Null when outside the plan. */
  todayActivity: { dayNumber: number; isShadow: boolean; sessionTitles: string[] } | null;
  /** Onboarding-plan window end (DOJ + journeyDays − 1, or a later extension). */
  planEndDate: string | null;
  /** 'active' = plan still running (or a live placement); 'completed' = plan ended. */
  planStatus: 'active' | 'completed';
  /** A coordinator-set later end date, when the plan was extended; else null. */
  extendedTo: string | null;
  reportingManagerName: string | null;
  reportingManagerCode: string | null;
  /** The reporting manager's own manager; null when unknown. */
  tradeHeadName: string | null;
  tradeHeadCode: string | null;
}

/** One resolved day of a joiner's onboarding plan (Grow → Onboarding Setup → Journey). */
interface JourneyDay {
  dayNumber: number;
  date: string;
  isToday: boolean;
  isPast: boolean;
  isShadowDay: boolean;
  sessions: Array<{
    id: number;
    title: string;
    startTime: string | null;
    durationMinutes: number | null;
    host: string | null;
    mode: string | null;
    meetingLink: string | null;
  }>;
}

type MatchTier = 'pool_city' | 'pool_state' | 'pool_project' | 'nearby_city' | 'nearby_state' | 'other_project';

interface BuddySuggestion {
  employeeCode: string;
  employeeName: string;
  designation: string;
  storeId: string;
  storeName: string;
  storeCode: string | null;
  city: string;
  state: string;
  tenureLabel: string | null;
  matchTier: MatchTier;
  fromPool: boolean;
  activeTrainees: number;
  onNotice: boolean;
  noticeLastWorkingDay: string | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const apiMessage = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const httpStatus = (err: unknown): number | null =>
  (err as { response?: { status?: number } })?.response?.status ?? null;

const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** Local-date ISO string. Never toISOString() — that shifts to UTC and can land
 *  on yesterday for an IST user before 05:30. */
const isoDay = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const addDays = (iso: string, n: number): string => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return isoDay(d);
};

/**
 * Each tier gets its own colour, icon and one-line rationale.
 *
 * These are genuinely different KINDS of recommendation — a curated buddy in the
 * same city is not the same offer as any colleague in the same state — so they
 * are styled as distinct categories rather than five identical grey headers.
 */
const TIER_META: Record<MatchTier, {
  title: string; why: string; icon: typeof Users;
  head: string; dot: string; badge: string;
}> = {
  pool_city: {
    title: 'Buddy Pool · same city', why: 'Curated buddies working in this joiner’s city — the best match',
    icon: Star, head: 'bg-violet-50 text-violet-900 border-violet-200', dot: 'bg-violet-500',
    badge: 'bg-violet-100 text-violet-700',
  },
  pool_state: {
    title: 'Buddy Pool · same state', why: 'Curated buddies elsewhere in this joiner’s state',
    icon: Users, head: 'bg-indigo-50 text-indigo-900 border-indigo-200', dot: 'bg-indigo-500',
    badge: 'bg-indigo-100 text-indigo-700',
  },
  pool_project: {
    title: 'Buddy Pool · elsewhere', why: 'Curated buddies outside this joiner’s city and state',
    icon: Users, head: 'bg-amber-50 text-amber-900 border-amber-200', dot: 'bg-amber-500',
    badge: 'bg-amber-100 text-amber-800',
  },
  nearby_city: {
    title: 'Other colleagues · same city', why: 'Not on the Buddy Pool, but working in the same city',
    icon: MapPin, head: 'bg-sky-50 text-sky-900 border-sky-200', dot: 'bg-sky-500',
    badge: 'bg-sky-100 text-sky-700',
  },
  nearby_state: {
    title: 'Other colleagues · same state', why: 'Not on the Buddy Pool, and in a different city',
    icon: MapPin, head: 'bg-slate-100 text-slate-800 border-slate-200', dot: 'bg-slate-400',
    badge: 'bg-slate-200 text-slate-700',
  },
  // Everyone else on the project. Offered last, and deliberately: the trainee has
  // to physically attend the buddy's store, so a different-state placement is a
  // conscious choice (relocation, thin local pool) — never the default.
  other_project: {
    title: 'Other colleagues · elsewhere', why: 'Outside this joiner’s city and state — the trainee would have to travel to that store',
    icon: MapPin, head: 'bg-gray-100 text-gray-700 border-gray-200', dot: 'bg-gray-400',
    badge: 'bg-gray-200 text-gray-700',
  },
};

const TIER_ORDER: MatchTier[] = ['pool_city', 'pool_state', 'pool_project', 'nearby_city', 'nearby_state', 'other_project'];

// A generous upper bound for the roster fetch. The server classifies each joiner
// active/completed from their own plan length and drops the long-completed, so the
// tab never needs a user-facing "joined within N days" filter any more.
const ONBOARDING_FETCH_DAYS = 90;

// ── Assignment modal ─────────────────────────────────────────────────────────

function AssignBuddyModal({
  projectId, joiner, editing, onClose, onSaved,
}: {
  projectId: number;
  joiner: NewJoiner;
  /** Present when changing a LIVE assignment rather than creating one. */
  editing?: ShadowAssignment | null;
  onClose: () => void;
  /** Closes the modal + refetches; an optional notice is surfaced on the tab. */
  onSaved: (notice?: string | null) => void;
}) {
  const [suggestions, setSuggestions] = useState<BuddySuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<BuddySuggestion | null>(null);
  // Viewing the buddy's sign-offs happens INLINE in this modal (not a second one).
  const [showSignoffs, setShowSignoffs] = useState(false);
  // Removing the buddy is confirmed BEFORE the modal closes, so a failed removal
  // stays visible here instead of the modal vanishing with the buddy still mapped.
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const handleRemove = async () => {
    if (!editing) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      const res = await onboardingAPI.endAssignment({
        projectId, id: editing.id, note: 'Removed from the Onboarding tab',
      });
      // Only close once the server confirms. Surface the note ONLY when the server
      // flags it as needing attention (we had placed them but the live row has
      // since changed). A joiner who simply kept their own store all along is the
      // normal path and must not raise a warning.
      onSaved(res.data?.meta?.needsAttention === true ? (res.data?.message ?? null) : null);
    } catch (err) {
      setRemoveError(apiMessage(err, 'Could not remove the buddy. Please try again.'));
      setRemoving(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await onboardingAPI.getSuggestions({
          projectId,
          employeeCode: joiner.employeeCode,
          city: joiner.city,
          state: joiner.state,
        });
        if (cancelled) return;
        const rows: BuddySuggestion[] = res.data?.data ?? [];
        setSuggestions(rows);
        if (editing) {
          // Keep the ASSIGNED buddy selected so "change only the dates" needs no
          // re-picking, and so the assigned buddy is always shown clearly — even
          // if they have dropped out of the current suggestions (moved store, left
          // the pool). Never silently fall back to a DIFFERENT top suggestion.
          const current = rows.find((r) => r.employeeCode === editing.buddyCode);
          setSelected(current ?? {
            employeeCode: editing.buddyCode,
            employeeName: editing.buddyName || editing.buddyCode,
            designation: '',
            storeId: editing.storeId ?? '',
            storeName: editing.storeName ?? '',
            storeCode: editing.storeCode ?? null,
            city: editing.storeCity ?? '',
            state: editing.storeState ?? '',
            tenureLabel: null,
            matchTier: 'pool_city',
            fromPool: false,
            activeTrainees: 0,
            onNotice: false,
            noticeLastWorkingDay: null,
          });
        }
        // CREATE mode: leave `selected` null on purpose — the coordinator chooses
        // a buddy first, and only then does the confirmation card appear. No
        // auto-selected top suggestion masquerading as their choice.
      } catch (err) {
        if (!cancelled) setError(apiMessage(err, 'Failed to load buddy suggestions.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, joiner.employeeCode, joiner.city, joiner.state]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return suggestions;
    return suggestions.filter(
      (s) =>
        s.employeeName.toLowerCase().includes(q) ||
        s.employeeCode.toLowerCase().includes(q) ||
        s.storeName.toLowerCase().includes(q) ||
        s.city.toLowerCase().includes(q),
    );
  }, [suggestions, search]);

  const grouped = useMemo(() => {
    const map = new Map<MatchTier, BuddySuggestion[]>();
    for (const s of filtered) {
      if (!map.has(s.matchTier)) map.set(s.matchTier, []);
      map.get(s.matchTier)!.push(s);
    }
    return TIER_ORDER.filter((t) => map.has(t)).map((t) => ({ tier: t, rows: map.get(t)! }));
  }, [filtered]);

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Shadow dates come from the Journey's per-day flags (derived server-side);
      // the modal no longer sends a window. Editing just swaps the buddy.
      if (editing) {
        await onboardingAPI.updateAssignment({
          projectId,
          id: editing.id,
          buddyCode: selected.employeeCode,
        });
      } else {
        await onboardingAPI.saveAssignment({
          projectId,
          employeeCode: joiner.employeeCode,
          employeeName: joiner.employeeName,
          dateOfJoining: joiner.dateOfJoining,
          buddyCode: selected.employeeCode,
        });
      }
      onSaved();
    } catch (err) {
      setSaveError(apiMessage(err, 'Failed to save the shadow training assignment.'));
    } finally {
      setSaving(false);
    }
  };

  // Once the plan is COMPLETE there is nothing left to assign — the modal becomes
  // a read-only record of who the buddy was and when. `history` is the assignment
  // to show (a live one if somehow still attached, else the last past one).
  const isCompleted = joiner.planStatus === 'completed';
  const history = editing ?? joiner.lastAssignment ?? null;
  const signoffAsg = editing ?? history;

  return (
    <Modal title={showSignoffs ? 'Sign-off responses' : isCompleted ? 'Onboarding record' : (editing ? 'Edit shadow training' : 'Assign a buddy')} onClose={onClose}>
      {showSignoffs && signoffAsg ? (
        <>
          <div className="space-y-4 px-5 py-4">
            <button
              type="button"
              onClick={() => setShowSignoffs(false)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:text-primary-800"
            >
              ← Back to placement
            </button>
            <SignoffResponsesBody projectId={projectId} assignment={signoffAsg} />
          </div>
          <div className="flex justify-end border-t border-gray-100 px-5 py-4">
            <button
              type="button"
              onClick={() => setShowSignoffs(false)}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
          </div>
        </>
      ) : (
      <>
      <div className="space-y-5 px-5 py-4">
        {/* WHO — the joiner, as labelled facts rather than a run-on line. */}
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-sm font-bold text-primary-700">
              {(joiner.employeeName || joiner.employeeCode).slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-base font-semibold text-gray-900">{joiner.employeeName}</span>
                {joiner.designation && (
                  <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-800">
                    {joiner.designation}
                  </span>
                )}
              </div>
              <div className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1.5 text-xs sm:grid-cols-3">
                <Fact label="Employee code" value={joiner.employeeCode} />
                <Fact label="Joined on" value={fmtDate(joiner.dateOfJoining)} />
                <Fact
                  label="Location"
                  value={[joiner.city, joiner.state].filter(Boolean).join(', ') || 'Unknown'}
                />
              </div>
            </div>
          </div>
          {!joiner.city && !joiner.state && (
            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
              This joiner has no location on their record, so buddies can't be ranked by area — everyone
              on the project is listed below, ungrouped by distance.
            </p>
          )}
        </div>

        {/* THE PLAN — the joiner's whole onboarding fortnight, right here so the
            coordinator sees it and the buddy assignment in one place. */}
        <OnboardingPlanSection projectId={projectId} joiner={joiner} />

        {/* Plan complete → a read-only record of the buddy + dates, with no option
            to (re)assign. */}
        {isCompleted && (
          <CompletedBuddyHistory
            assignment={history}
            onViewSignoffs={history ? () => setShowSignoffs(true) : undefined}
          />
        )}

        {/* WHO WITH — the shadow days themselves come from the onboarding plan
            above (the activities flagged as shadow-training days); nothing about
            the window is chosen here. Hidden once the plan is complete. */}
        {!isCompleted && (
        <>
        <section>
          <SectionHeading n={1} title="Choose a buddy" />

          {/* The current buddy, restated plainly and labelled — on edit this is the
              ASSIGNED buddy (shown up front, not just highlighted in the list); on
              create it appears only AFTER the coordinator picks one. */}
          {selected ? (
            <div className="mb-3 flex items-start gap-3 rounded-xl border border-primary-200 bg-primary-50 p-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary-600" />
              <div className="min-w-0 flex-1 text-sm">
                <div className="text-[10px] font-bold uppercase tracking-wide text-primary-700">
                  {editing ? 'Assigned buddy' : 'Selected buddy'}
                </div>
                <div className="font-semibold text-gray-900">{selected.employeeName}</div>
                <div className="mt-0.5 text-xs text-gray-600">
                  {joiner.employeeName} will work at <strong>{selected.storeName}</strong>
                  {selected.city ? ` · ${selected.city}` : ''}
                </div>
              </div>
              {selected.onNotice && (
                <span className="shrink-0 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700">
                  On notice
                </span>
              )}
            </div>
          ) : (
            <div className="mb-3 rounded-xl border border-dashed border-gray-300 bg-gray-50 px-3 py-2.5 text-xs text-gray-500">
              Pick a buddy from the suggestions below — nothing is selected yet.
            </div>
          )}

          {loading ? (
            <div className="rounded-xl border border-gray-200 py-10 text-center text-sm text-gray-400">
              <InlineLoader label="Loading suggestions…" className="flex justify-center" />
            </div>
          ) : error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          ) : grouped.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
              {suggestions.length === 0
                ? 'No deployed colleague was found on this project. A buddy must already be deployed to a store, since the trainee attends it — deploy a colleague, or add someone to the Buddy Pool under Grow → Onboarding Setup.'
                : 'No suggestion matches your search.'}
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200">
              {/* Search sits INSIDE the list card and spans it, so the field and
                  the results it filters share one edge instead of floating off
                  to the side of the heading. */}
              <div className="border-b border-gray-200 bg-gray-50/70 p-3">
                <SearchBox value={search} onChange={setSearch} placeholder="Search by name, employee code or store..." />
              </div>
              <div className="max-h-72 overflow-y-auto">
              {grouped.map(({ tier, rows }) => {
                const meta = TIER_META[tier];
                const TierIcon = meta.icon;
                return (
                <div key={tier}>
                  <div className={`sticky top-0 z-10 border-y px-3 py-2 ${meta.head}`}>
                    <div className="flex items-center gap-2">
                      <TierIcon className="h-3.5 w-3.5 shrink-0" />
                      <span className="text-[11px] font-bold uppercase tracking-wide">{meta.title}</span>
                      <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold ${meta.badge}`}>
                        {rows.length}
                      </span>
                    </div>
                    <p className="mt-0.5 pl-[22px] text-[11px] font-normal opacity-80">{meta.why}</p>
                  </div>
                  <ul className="divide-y divide-gray-100">
                    {rows.map((s) => {
                      const isSelected = selected?.employeeCode === s.employeeCode;
                      return (
                        <li key={s.employeeCode}>
                          <button
                            type="button"
                            onClick={() => setSelected(s)}
                            className={`flex w-full items-center gap-3 border-l-[3px] px-3 py-2.5 text-left transition-colors ${
                              isSelected
                                ? 'border-l-primary-600 bg-primary-50'
                                : 'border-l-transparent hover:bg-gray-50'
                            }`}
                          >
                            <span
                              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
                                isSelected ? 'border-primary-600 bg-primary-600' : 'border-gray-300'
                              }`}
                            >
                              {isSelected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="flex flex-wrap items-center gap-2">
                                <span className="truncate text-sm font-medium text-gray-900">{s.employeeName}</span>
                                <EmployeeTags
                                  designation={s.designation}
                                  onNotice={s.onNotice}
                                  lastWorkingDay={s.noticeLastWorkingDay}
                                />
                                {s.activeTrainees > 0 && (
                                  // Not a blocker, just load: a buddy already
                                  // hosting someone can host another, but the
                                  // coordinator should know before picking them.
                                  <span className="shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                                    {s.activeTrainees} trainee{s.activeTrainees === 1 ? '' : 's'}
                                  </span>
                                )}
                              </span>
                              <span className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-gray-500">
                                <span>{s.employeeCode}</span>
                                {s.tenureLabel && <span>{s.tenureLabel}</span>}
                                <span className="flex items-center gap-1">
                                  <StoreIcon className="h-3 w-3" />
                                  {s.storeName}
                                </span>
                                <span className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3" />
                                  {[s.city, s.state].filter(Boolean).join(', ')}
                                </span>
                              </span>
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                );
              })}
              </div>
            </div>
          )}
        </section>

        {/* Manage an existing placement — its sign-off answers and ending it early
            live here too, so this modal is the single home for a joiner's
            onboarding rather than a scatter of separate row actions. */}
        {editing && (
          <section className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-auto text-xs font-semibold uppercase tracking-wide text-gray-500">Manage placement</span>
              <button
                type="button"
                onClick={() => setShowSignoffs(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
              >
                <ClipboardList className="h-3.5 w-3.5" /> View sign-offs
              </button>
              <button
                type="button"
                onClick={handleRemove}
                disabled={removing}
                title="Remove this buddy — ends the placement and returns the joiner to their own store. Their onboarding continues; you can assign a new buddy."
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <UserMinus className="h-3.5 w-3.5" /> {removing ? 'Removing…' : 'Remove buddy'}
              </button>
            </div>
            {removeError && (
              <p className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{removeError}</p>
            )}
          </section>
        )}

        {saveError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{saveError}</div>
        )}
        </>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-5 py-4">
        {isCompleted ? (
          <>
            <p className="text-xs text-gray-500">This onboarding plan is complete — shown for reference only.</p>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </>
        ) : (
          <>
            <p className="text-xs text-gray-500">
              {!selected
                ? 'Pick a buddy to continue.'
                : `Shadow training at ${selected.storeName}, on the plan's shadow-training days.`}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!selected || saving}
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Save assignment'}
              </button>
            </div>
          </>
        )}
      </div>
      </>
      )}
    </Modal>
  );
}

/** Read-only record of a completed onboarding's buddy placement — who, where, when
 *  — shown INSTEAD of the assign UI once the plan is over. */
function CompletedBuddyHistory({
  assignment, onViewSignoffs,
}: {
  assignment: ShadowAssignment | null;
  onViewSignoffs?: () => void;
}) {
  return (
    <section>
      <SectionHeading n={1} title="Buddy & shadow training" />
      {assignment ? (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-sm font-bold text-primary-700">
              {(assignment.buddyName || assignment.buddyCode).slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Buddy</div>
              <div className="text-base font-semibold text-gray-900">{assignment.buddyName || assignment.buddyCode}</div>
              <div className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1.5 text-xs sm:grid-cols-3">
                <Fact label="Buddy code" value={assignment.buddyCode} />
                <Fact label="Shadow store" value={assignment.storeName || '—'} />
                <Fact label="Shadow training" value={`${fmtDate(assignment.startDate)} → ${fmtDate(assignment.endDate)}`} />
              </div>
            </div>
          </div>
          {onViewSignoffs && (
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={onViewSignoffs}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
              >
                <ClipboardList className="h-3.5 w-3.5" /> View sign-offs
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
          No buddy was assigned during this joiner's onboarding.
        </div>
      )}
    </section>
  );
}

/** A small labelled fact in the joiner summary. */
function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-gray-400">{label}</div>
      <div className="mt-0.5 text-gray-900">{value}</div>
    </div>
  );
}

/** Numbered section heading, so the modal reads as an ordered task. */
function SectionHeading({ n, title }: { n: number; title: string }) {
  return (
    <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-900">
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-900 text-[10px] font-bold text-white">
        {n}
      </span>
      {title}
    </h4>
  );
}


/**
 * What the buddy actually recorded, day by day.
 *
 * The capture path (mobile) and the review path (here) were built at different
 * times; without this the answers existed only in the database. Grouped by day
 * rather than by question because that is how they were entered, and a missing
 * day is itself the signal a coordinator is looking for.
 */
// The buddy's daily sign-off answers for one placement — a self-contained body
// (no modal chrome) so it renders INLINE inside the assignment modal rather than
// stacking a second modal on top of it.
function SignoffResponsesBody({
  projectId, assignment,
}: { projectId: number; assignment: ShadowAssignment }) {
  const [data, setData] = useState<{
    days: Array<{
      date: string; dayNumber: number; submittedAt: string | null;
      answers: Array<{ questionId: string; questionText: string; questionType: string; answer: unknown }>;
    }>;
    signedOffDays: number;
    windowLength: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await onboardingAPI.getAssignmentResponses({ projectId, id: assignment.id });
        if (!cancelled) setData(res.data?.data ?? null);
      } catch (err) {
        if (!cancelled) setError(apiMessage(err, 'Failed to load the sign-off responses.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, assignment.id]);

  const renderAnswer = (a: { answer: unknown; questionType: string }) => {
    if (a.answer === null || a.answer === undefined || a.answer === '') {
      return <span className="text-gray-400">No answer</span>;
    }
    if (Array.isArray(a.answer)) return <span>{a.answer.map(String).join(', ')}</span>;
    if (a.questionType === 'rating_1_5') {
      return (
        <span className="inline-flex items-center rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-bold text-primary-700">
          {String(a.answer)}
        </span>
      );
    }
    return <span>{String(a.answer)}</span>;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
        <span className="text-gray-600">
          Buddy <strong className="text-gray-900">{assignment.buddyName || assignment.buddyCode}</strong>
        </span>
        <span className="text-gray-600">
          Store <strong className="text-gray-900">{assignment.storeName || '—'}</strong>
        </span>
        {data && (
          <span className="ml-auto rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700">
            {data.signedOffDays} of {data.windowLength} days signed off
          </span>
        )}
      </div>

      {loading ? (
        <div className="py-10 text-center text-sm text-gray-400"><InlineLoader label="Loading responses…" className="flex justify-center" /></div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : !data || data.days.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center">
          <p className="text-sm font-medium text-gray-700">Nothing recorded yet</p>
          <p className="mt-1 text-xs text-gray-500">
            The buddy fills this in from the mobile app. Answers appear here as soon as they submit a day.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.days.map((d) => (
            <div key={d.date} className="overflow-hidden rounded-xl border border-gray-200">
              <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 bg-gray-50 px-4 py-2">
                <span className="text-xs font-bold uppercase tracking-wide text-gray-700">
                  Day {d.dayNumber}
                </span>
                <span className="text-xs text-gray-500">{fmtDate(d.date)}</span>
                {d.submittedAt && (
                  <span className="ml-auto flex items-center gap-1 text-[11px] text-green-700">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Submitted {new Date(d.submittedAt).toLocaleString('en-IN', {
                      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                    })}
                  </span>
                )}
              </div>
              <dl className="divide-y divide-gray-100">
                {d.answers.map((a) => (
                  <div key={a.questionId} className="grid grid-cols-1 gap-1 px-4 py-2.5 sm:grid-cols-3 sm:gap-4">
                    <dt className="text-xs text-gray-600 sm:col-span-2">{a.questionText}</dt>
                    <dd className="text-sm font-medium text-gray-900">{renderAnswer(a)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Onboarding plan preview (shown inside the assign/manage modal) ────────────

/**
 * One joiner's resolved day-by-day onboarding plan, shadow days highlighted.
 * Lives inside the modal beneath the joiner card, so a coordinator sees the whole
 * fortnight in the same place they assign or change the buddy. Collapsible to keep
 * the modal compact; fetched lazily when the modal opens.
 */
function OnboardingPlanSection({ projectId, joiner }: { projectId: number; joiner: NewJoiner }) {
  const a = joiner.assignment;
  // A string, not the array, so the fetch below re-runs only when the days change.
  const shadowDatesKey = (a?.shadowDates ?? []).join(',');
  const [open, setOpen] = useState(true);
  const [days, setDays] = useState<JourneyDay[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!joiner.dateOfJoining) { setLoading(false); setDays([]); return; }
      setLoading(true);
      setError(null);
      try {
        const res = await onboardingAPI.getJoinerJourney({
          projectId,
          doj: joiner.dateOfJoining,
          // When assigned, mark THIS joiner's actual shadow days — the same ones
          // their mobile plan shows. Gaps matter, so send the day list; the
          // start/end window is only used for legacy rows without one.
          shadowDates: shadowDatesKey ? shadowDatesKey.split(',') : null,
          shadowStart: a?.startDate,
          shadowEnd: a?.endDate,
        });
        if (!cancelled) setDays(res.data?.data ?? []);
      } catch (err) {
        if (!cancelled) setError(apiMessage(err, 'Could not load the onboarding plan.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, joiner.dateOfJoining, a?.startDate, a?.endDate, shadowDatesKey]);

  const shadowCount = (days ?? []).filter((d) => d.isShadowDay).length;

  return (
    <section>
      <button type="button" onClick={() => setOpen((v) => !v)} className="mb-2 flex w-full items-center gap-2 text-left">
        <CalendarDays className="h-4 w-4 text-primary-600" />
        <span className="text-sm font-semibold text-gray-900">Onboarding plan</span>
        {days && days.length > 0 && (
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
            {days.length} days{shadowCount > 0 ? ` · ${shadowCount} shadow` : ''}
          </span>
        )}
        <ChevronDown className={`ml-auto h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        loading ? (
          <div className="rounded-xl border border-gray-200 py-6 text-center text-sm text-gray-400"><InlineLoader label="Loading plan…" className="flex justify-center" /></div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        ) : !days || days.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-500">
            No journey is set up for this project yet. Build the day-by-day plan under{' '}
            <span className="font-medium text-gray-700">Grow → Onboarding Setup → Journey</span>.
          </div>
        ) : (
          <ol className="max-h-64 space-y-1.5 overflow-y-auto rounded-xl border border-gray-100 bg-gray-50/60 p-2">
            {days.map((d) => (
              <li
                key={d.dayNumber}
                className={`flex gap-3 rounded-lg border px-3 py-2 ${
                  d.isShadowDay
                    ? 'border-violet-200 bg-violet-50'
                    : d.isToday
                      ? 'border-blue-200 bg-blue-50'
                      : 'border-gray-100 bg-white'
                }`}
              >
                <div className="w-14 shrink-0">
                  <div className="text-xs font-bold text-gray-700">Day {d.dayNumber + 1}</div>
                  <div className="text-[10px] text-gray-400">{fmtDate(d.date)}</div>
                </div>
                <div className="min-w-0 flex-1">
                  {d.isShadowDay && (
                    <span className="mb-1 inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold text-violet-700">
                      <Users className="h-3 w-3" /> Shadow training
                    </span>
                  )}
                  {d.sessions.length === 0 ? (
                    !d.isShadowDay && <span className="text-xs text-gray-400">No sessions</span>
                  ) : (
                    <div className="space-y-1">
                      {d.sessions.map((sn) => (
                        <div key={sn.id} className="text-xs text-gray-700">
                          <span className="font-semibold text-gray-900">{sn.title}</span>
                          {sn.startTime && <span className="text-gray-500"> · {sn.startTime}</span>}
                          {sn.durationMinutes != null && <span className="text-gray-500"> · {sn.durationMinutes} min</span>}
                          {sn.host && <span className="text-gray-500"> · {sn.host}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )
      )}
    </section>
  );
}

// ── Extend onboarding modal ───────────────────────────────────────────────────

/**
 * Push a joiner's onboarding-plan end to a later date. Moves them from Completed
 * back to Active (or lengthens an already-active plan). The new date must be after
 * their current plan end.
 */
function ExtendPlanModal({
  projectId, joiner, onClose, onSaved,
}: {
  projectId: number;
  joiner: NewJoiner;
  onClose: () => void;
  onSaved: () => void;
}) {
  const today = isoDay(new Date());
  // The floor is the day after the current plan end (or today), so an extension
  // can only ever push the plan LATER.
  const currentEnd = joiner.planEndDate && joiner.planEndDate >= today ? joiner.planEndDate : today;
  const minDate = addDays(currentEnd, 1);
  const [date, setDate] = useState(addDays(currentEnd, 7));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!date) return;
    setSaving(true);
    setError(null);
    try {
      await onboardingAPI.extendPlan({
        projectId, employeeCode: joiner.employeeCode, extendedTo: date, note: note.trim() || undefined,
      });
      onSaved();
    } catch (err) {
      setError(apiMessage(err, 'Failed to extend the onboarding plan.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    // Compact dialog — this only collects a date + an optional note, so it uses a
    // narrow width rather than the full modal shell.
    <Modal title="Extend onboarding" onClose={onClose} maxWidth="max-w-md">
      <div className="space-y-4">
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
          <div className="text-sm font-semibold text-gray-900">{joiner.employeeName}</div>
          <div className="text-xs text-gray-500">
            {joiner.employeeCode} · plan {joiner.planStatus === 'completed' ? 'ended' : 'ends'}{' '}
            <strong className="text-gray-700">{fmtDate(joiner.planEndDate)}</strong>
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="ext-date">Extend onboarding until</label>
          <input
            id="ext-date"
            type="date"
            value={date}
            min={minDate}
            onChange={(e) => setDate(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
          <p className="mt-1.5 flex items-start gap-1.5 text-xs text-gray-500">
            <CalendarPlus className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
            <span>They move back to the <strong>Active</strong> tab and keep onboarding through this date.</span>
          </p>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="ext-note">Note (optional)</label>
          <input
            id="ext-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
            placeholder="Why is onboarding being extended?"
          />
        </div>
        {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !date}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {saving ? 'Saving…' : 'Extend onboarding'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Checklist & sign-off history ─────────────────────────────────────────────
//
// Per joiner, per plan day: was their onboarding checklist filled, and was the
// buddy's (Sr iPro) sign-off filled? Shapes mirror the API's
// onboarding-compliance.service, which applies the SAME due-rules the app uses to
// ask — so this can never disagree with the Tasks list or the sign-out block.

type ComplianceStatus = 'filled' | 'missed' | 'due_today' | 'upcoming' | 'not_due' | 'no_buddy';
interface ComplianceCell { status: ComplianceStatus; formTitle: string | null; submittedAt: string | null }
interface ComplianceDay {
  dayNumber: number;
  date: string;
  activities: string[];
  isShadowDay: boolean;
  checklist: ComplianceCell;
  signoff: ComplianceCell & { buddyName: string | null };
}
interface ComplianceSummary {
  checklistDue: number; checklistFilled: number; checklistMissed: number;
  signoffDue: number; signoffFilled: number; signoffMissed: number;
}
interface JoinerCompliance { employeeCode: string; days: ComplianceDay[]; summary: ComplianceSummary }

/** One label + colour per status — colour the exception: red = not filled. */
const COMPLIANCE_META: Record<ComplianceStatus, { label: string; cls: string }> = {
  filled: { label: 'Filled', cls: 'bg-emerald-50 text-emerald-700' },
  missed: { label: 'Not filled', cls: 'bg-red-50 text-red-700' },
  due_today: { label: 'Due today', cls: 'bg-amber-50 text-amber-800' },
  upcoming: { label: 'Upcoming', cls: 'bg-gray-100 text-gray-500' },
  not_due: { label: 'Not required', cls: 'text-gray-300' },
  no_buddy: { label: 'No buddy assigned', cls: 'bg-gray-100 text-gray-500' },
};

/** A submission time in IST, e.g. "12 Sept, 6:40 pm". */
const fmtSubmitted = (ts: string | null): string => {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit',
  });
};

/** Today in IST as YYYY-MM-DD (en-CA formats as ISO). */
const todayIst = (): string => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

/** What the compliance endpoint needs per joiner — facts the row already holds. */
const compliancePayload = (j: NewJoiner) => ({
  employeeCode: j.employeeCode,
  dateOfJoining: j.dateOfJoining,
  planEndDate: j.planEndDate,
});

// ── Onboarding score (worklist column, chips, export) ────────────────────────
//
// The score itself — buddy ratings → % → day scores → one onboarding score, and
// the deploy/extend advice — is computed server-side (onboarding-score.service);
// the pill, caption and labels come from ./onboardingScore so this column and the
// score modal can never disagree. The tab only loads it, sorts, groups and exports.

/** Joiners per scores request — the server caps a batch at 500 like compliance. */
const SCORE_CHUNK = 500;

type ScoreChipKey = OnboardingScoreGroup;

/** Which joiners each score group holds, and its chip's dot. The three advice groups
 *  only hold joiners nobody has decided on yet. */
const SCORE_GROUP_RULES: Record<ScoreChipKey, { dot: string; match: (s: JoinerScore) => boolean }> = {
  ready: { dot: 'bg-emerald-500', match: (s) => !s.latestDecision && s.advice === 'deploy' },
  extend: { dot: 'bg-amber-500', match: (s) => !s.latestDecision && s.advice === 'extend' },
  not_enough: { dot: 'bg-gray-400', match: (s) => !s.latestDecision && (s.advice === 'not_enough' || s.advice === 'no_ratings') },
  in_training: { dot: 'bg-blue-500', match: (s) => s.trainingState === 'in_progress' || s.trainingState === 'not_started' },
  decided: { dot: 'bg-slate-500', match: (s) => !!s.latestDecision },
};

/** The quick filters above the table. Each is a question a coordinator asks of the
 *  worklist. The label and hover text are kept with the address that opens each group
 *  (lists/onboarding), so the chip and page search name it alike. */
const SCORE_CHIPS: Array<{ key: ScoreChipKey; label: string; dot: string; title: string; match: (s: JoinerScore) => boolean }> =
  ONBOARDING_SCORE_GROUPS.map((g) => ({ key: g.key, label: g.label, title: g.title, ...SCORE_GROUP_RULES[g.key] }));

const scoreKey = (employeeCode: string): string => employeeCode.trim().toLowerCase();

/** The export's "Score advice": the advice once training is complete, else where
 *  training stands (the same words the pill uses). */
const scoreAdviceText = (s: JoinerScore): string => {
  if (s.advice) return ADVICE_META[s.advice].label;
  if (s.trainingState === 'complete') return ADVICE_META.no_ratings.label;
  return s.trainingState === 'no_training' ? 'No shadow training' : s.trainingState === 'not_started' ? 'Not started' : 'In progress';
};

/** The export's "Score decision", e.g. "Extended to 2026-10-09 (decided 2026-09-25)". */
const scoreDecisionText = (d: ScoreDecision | null): string => {
  if (!d) return '';
  const what = d.decision === 'deploy' ? 'Cleared for deployment' : d.extendedTo ? `Extended to ${d.extendedTo}` : 'Extended';
  return `${what} (decided ${istDay(d.decidedAt)})`;
};

type ScoresStatus = 'loading' | 'ready' | 'error';

/**
 * One row's score: the %, the pill (a recorded decision wins over the advice) and
 * the days caption. A single plain text button — the row's own click opens the
 * buddy modal, so this one stops propagation and opens the score instead. Its
 * accessible name starts with that visible text (WCAG 2.5.3), so a screen reader
 * hears the score and a voice user can say what they see.
 */
function ScoreCell({ joiner, score, status, onOpen }: {
  joiner: NewJoiner; score: JoinerScore | undefined; status: ScoresStatus; onOpen: () => void;
}) {
  if (status === 'loading') return <span className="text-gray-400" title="Loading the onboarding score">…</span>;
  if (!score) {
    return (
      <span
        className="text-gray-400"
        title={status === 'error'
          ? 'Onboarding scores couldn’t load. Use “Try again” above the table.'
          : 'No onboarding score for this joiner.'}
      >
        —
      </span>
    );
  }
  const pill = scorePill(score);
  const caption = scoreCaption(score);
  const pct = formatScore(score.scorePct, score.passMark);
  const visible = `${score.scorePct != null ? `${pct} ` : ''}${pill.label}${caption ? `, ${caption}` : ''}`;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onOpen(); }}
      aria-label={`${visible}. Open onboarding score for ${joiner.employeeName}`}
      title={pill.title}
      className="-mx-1.5 -my-1 block rounded-md px-1.5 py-1 text-left transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
    >
      <span className="flex flex-wrap items-center gap-1.5">
        <span className="font-semibold text-gray-900">{pct}</span>
        <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${TONE_PILL[pill.tone]}`}>
          {pill.label}
        </span>
      </span>
      {caption && <span className="mt-0.5 block whitespace-nowrap text-xs text-gray-500">{caption}</span>}
    </button>
  );
}

/**
 * One history cell. A Filled one also carries View, which opens what was filled
 * (OnboardingAnswersView) inside the same pop-up. Its accessible name starts with the
 * visible "View" (WCAG 2.5.3) and says which form and day it opens.
 */
function ComplianceBadge({ cell, onView, viewLabel, viewKey }: {
  cell: ComplianceCell; onView?: () => void; viewLabel?: string; viewKey?: string;
}) {
  if (cell.status === 'not_due') return <span className="text-xs text-gray-300">—</span>;
  const meta = COMPLIANCE_META[cell.status];
  const canView = cell.status === 'filled' && !!onView;
  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
        <span
          title={cell.formTitle ?? undefined}
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}
        >
          {cell.status === 'filled' && <CheckCircle2 className="h-3 w-3" />}
          {meta.label}
        </span>
        {canView && (
          <button
            type="button"
            onClick={onView}
            aria-label={viewLabel}
            data-answers-key={viewKey}
            className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[11px] font-semibold text-primary-600 transition-colors hover:bg-primary-50 hover:text-primary-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <Eye className="h-3.5 w-3.5" aria-hidden="true" />
            View
          </button>
        )}
      </div>
      {cell.status === 'filled' && cell.submittedAt && (
        <div className="mt-0.5 text-[10px] text-gray-400">{fmtSubmitted(cell.submittedAt)}</div>
      )}
    </div>
  );
}

/** A compliance cell's sort value = the badge label it shows (not_due shows "—" → blank). */
const complianceSortLabel = (cell: ComplianceCell): string | null =>
  cell.status === 'not_due' ? null : COMPLIANCE_META[cell.status].label;

/** The day-plan table's Plan cell as text: the shadow-training chip, then the activities. */
const compliancePlanText = (d: ComplianceDay): string =>
  [d.isShadowDay ? 'Shadow training' : '', ...d.activities].filter(Boolean).join(' · ');

function ComplianceSummaryTile({ title, filled, due, missed, emptyText }: {
  title: string; filled: number; due: number; missed: number; emptyText: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{title}</div>
      {due === 0 ? (
        <div className="mt-1 text-sm text-gray-400">{emptyText}</div>
      ) : (
        <div className="mt-1 flex items-baseline gap-2">
          <span className="text-xl font-bold text-gray-900">{filled}/{due}</span>
          <span className="text-xs text-gray-500">filled so far</span>
          {missed > 0 && (
            <span className="ml-auto rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">
              {missed} not filled
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/** The Filled cell whose answers are open in the history pop-up. */
interface OpenAnswers { form: OnboardingForm; date: string; dayLabel: number }
const answersKey = (form: OnboardingForm, date: string): string => `${form}|${date}`;

function ComplianceHistoryModal({ projectId, joiner, onClose }: {
  projectId: number; joiner: NewJoiner; onClose: () => void;
}) {
  const [data, setData] = useState<JoinerCompliance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // View swaps the table for one cell's answers INSIDE this pop-up; Back swaps it back
  // at the scroll it was left at, with focus on the View that was pressed.
  const [openAnswers, setOpenAnswers] = useState<OpenAnswers | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const tableScroll = useRef(0);
  const returnTo = useRef<string | null>(null);
  const viewAnswers = (form: OnboardingForm, d: ComplianceDay) => {
    tableScroll.current = bodyRef.current?.scrollTop ?? 0;
    returnTo.current = answersKey(form, d.date);
    setOpenAnswers({ form, date: d.date, dayLabel: d.dayNumber + 1 });
  };
  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    if (openAnswers) {
      body.scrollTop = 0;
      return;
    }
    if (returnTo.current === null) return;
    body.scrollTop = tableScroll.current;
    const key = returnTo.current;
    returnTo.current = null;
    body.querySelector<HTMLElement>(`[data-answers-key="${key}"]`)?.focus({ preventScroll: true });
  }, [openAnswers]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await onboardingAPI.getCompliance({ projectId, joiners: [compliancePayload(joiner)] });
        if (!cancelled) setData(((res.data?.data ?? []) as JoinerCompliance[])[0] ?? null);
      } catch (err) {
        if (!cancelled) setError(apiMessage(err, 'Could not load this joiner’s history.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, joiner.employeeCode, joiner.dateOfJoining, joiner.planEndDate]);

  const today = todayIst();
  const s = data?.summary;
  const everHadBuddy = data?.days.some((d) => !!d.signoff.buddyName) ?? false;
  // Column sorting for the day-plan table; unsorted = the plan's own day order.
  const daySort = useTableSort<ComplianceDay>(data?.days ?? [], {
    day: (d) => d.dayNumber,
    plan: (d) => compliancePlanText(d),
    checklist: (d) => complianceSortLabel(d.checklist),
    signoff: (d) => complianceSortLabel(d.signoff),
  });

  return (
    <Modal title="Checklist & sign-off history" onClose={onClose} maxWidth="max-w-5xl" bodyRef={bodyRef}>
      <div className="space-y-4 overflow-y-auto px-5 py-4">
        <div>
          <div className="text-base font-semibold text-gray-900">{joiner.employeeName}</div>
          <div className="text-xs text-gray-500">
            {joiner.employeeCode} · joined {fmtDate(joiner.dateOfJoining)}
            {joiner.planEndDate ? ` · plan ${joiner.planStatus === 'completed' ? 'ended' : 'ends'} ${fmtDate(joiner.planEndDate)}` : ''}
          </div>
        </div>

        {openAnswers ? (
          <OnboardingAnswersView
            projectId={projectId}
            employeeCode={joiner.employeeCode}
            joinerName={joiner.employeeName}
            form={openAnswers.form}
            date={openAnswers.date}
            dayLabel={openAnswers.dayLabel}
            onBack={() => setOpenAnswers(null)}
          />
        ) : loading ? (
          <div className="rounded-xl border border-gray-200 py-10 text-center text-sm text-gray-400"><InlineLoader label="Loading history…" className="flex justify-center" /></div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        ) : !data || data.days.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
            This joiner has no joining date on record, so their plan days can’t be worked out.
          </div>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <ComplianceSummaryTile
                title="Onboarding checklist"
                filled={s!.checklistFilled} due={s!.checklistDue} missed={s!.checklistMissed}
                emptyText="No checklist has been due yet"
              />
              <ComplianceSummaryTile
                title="Sr iPro sign-off"
                filled={s!.signoffFilled} due={s!.signoffDue} missed={s!.signoffMissed}
                emptyText={everHadBuddy ? 'No sign-off has been due yet' : 'No buddy assigned yet'}
              />
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-500">
                  <tr>
                    <SortTh label="Day" sortKey="day" sort={daySort} className="whitespace-nowrap px-4 py-2.5" />
                    <SortTh label="Plan" sortKey="plan" sort={daySort} className="px-4 py-2.5" />
                    <SortTh label={FORM_LABEL.checklist} sortKey="checklist" sort={daySort} className="whitespace-nowrap px-4 py-2.5" />
                    <SortTh label={FORM_LABEL.signoff} sortKey="signoff" sort={daySort} className="whitespace-nowrap px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {daySort.sorted.map((d) => (
                    <tr key={d.date} className={d.date === today ? 'bg-primary-50/50' : ''}>
                      <td className="whitespace-nowrap px-4 py-2.5 align-top">
                        <div className="font-medium text-gray-900">
                          Day {d.dayNumber + 1}
                          {d.date === today && (
                            <span className="ml-1.5 rounded-full bg-primary-100 px-1.5 py-0.5 text-[10px] font-semibold text-primary-700">Today</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">{fmtDate(d.date)}</div>
                      </td>
                      <td className="px-4 py-2.5 align-top">
                        {d.isShadowDay && (
                          <span className="mb-1 inline-flex rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700">
                            Shadow training
                          </span>
                        )}
                        <div className="text-xs text-gray-600">
                          {d.activities.length > 0 ? d.activities.join(' · ') : <span className="text-gray-300">—</span>}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 align-top">
                        <ComplianceBadge
                          cell={d.checklist}
                          onView={() => viewAnswers('checklist', d)}
                          viewLabel={`View answers, ${FORM_LABEL.checklist}, Day ${d.dayNumber + 1}`}
                          viewKey={answersKey('checklist', d.date)}
                        />
                      </td>
                      <td className="px-4 py-2.5 align-top">
                        <ComplianceBadge
                          cell={d.signoff}
                          onView={() => viewAnswers('signoff', d)}
                          viewLabel={`View answers, ${FORM_LABEL.signoff}, Day ${d.dayNumber + 1}`}
                          viewKey={answersKey('signoff', d.date)}
                        />
                        {d.signoff.buddyName && d.signoff.status !== 'not_due' && (
                          <div className="mt-0.5 text-[10px] text-gray-400">by {d.signoff.buddyName}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ── Tab ──────────────────────────────────────────────────────────────────────

// ONE unified list: recent joiners PLUS anyone with a live shadow assignment who
// may have aged out of the joiner window (day 7+ of their shadow week). Without
// the second half, collapsing the old "Shadow Training" tab would hide trainees
// mid-placement. Orphan assignments are rebuilt into joiner rows from the area +
// joining date the assignment carries for exactly this case.
const mergeJoinerRows = (joiners: NewJoiner[], assignments: ShadowAssignment[]): NewJoiner[] => {
  const byCode = new Map<string, NewJoiner>();
  for (const j of joiners) byCode.set(j.employeeCode, j);
  for (const a of assignments) {
    if (a.status !== 'active' && a.status !== 'scheduled') continue;
    if (byCode.has(a.employeeCode)) continue;
    byCode.set(a.employeeCode, {
      employeeCode: a.employeeCode,
      employeeName: a.employeeName || a.employeeCode,
      designation: '',
      city: a.joinerCity ?? '',
      state: a.joinerState ?? '',
      dateOfJoining: a.joinerDateOfJoining ?? null,
      daysSinceJoining: null,
      isDeployed: true,
      currentStoreName: a.storeName,
      assignment: a,
      lastAssignment: null,
      hasCityBuddyMatch: false,
      batchInitiated: false,
      todayActivity: null,
      // A live shadow placement means they're onboarding right now → Active.
      planEndDate: a.endDate ?? null,
      planStatus: 'active',
      extendedTo: null,
      reportingManagerName: null,
      reportingManagerCode: null,
      tradeHeadName: null,
      tradeHeadCode: null,
    });
  }
  return Array.from(byCode.values());
};

/** Nobody has paired this joiner yet: no buddy, and so no store. While their plan is
 *  running that also means they cannot sign in to the mobile app. */
const waitingForBuddy = (j: NewJoiner): boolean => !j.assignment && !j.isDeployed;

// ── Filters ──────────────────────────────────────────────────────────────────

type BuddyFilter = 'waiting' | 'has';
type PlanFilter = 'initiated' | 'not_initiated';

/** What the Filters window has chosen. An empty list is that filter off. */
interface JoinerFilters {
  buddy: BuddyFilter[];
  plan: PlanFilter[];
  /** Assistant Managers (the reporting managers) and Trade Heads, by `personKey`. */
  am: string[];
  th: string[];
}
type JoinerFilterKey = keyof JoinerFilters;

const NO_FILTERS: JoinerFilters = { buddy: [], plan: [], am: [], th: [] };
const FILTER_KEYS: JoinerFilterKey[] = ['buddy', 'plan', 'am', 'th'];
const FILTER_LABEL: Record<JoinerFilterKey, string> = {
  buddy: 'Buddy', plan: 'Onboarding plan', am: 'Assistant Manager', th: 'Trade Head',
};

// "Waiting for a buddy" is waitingForBuddy itself: the rule ?list=no-buddy and the home
// screen's count (isWaitingForBuddy on the server) use. Everyone else has a buddy or is
// already at a store of their own, which is what the other choice says.
const BUDDY_OPTIONS: Array<{ value: BuddyFilter; label: string }> = [
  { value: 'waiting', label: NO_BUDDY_LIST.label },
  { value: 'has', label: 'Has a buddy or store' },
];
const PLAN_OPTIONS: Array<{ value: PlanFilter; label: string }> = [
  { value: 'initiated', label: 'Initiated' },
  { value: 'not_initiated', label: 'Not initiated' },
];

/** Buddy alone, set to "Waiting for a buddy": the list ?list=no-buddy names. */
const isWaitingOnly = (buddy: BuddyFilter[]): boolean => buddy.length === 1 && buddy[0] === 'waiting';

/** A person's filter value: their code, or their name on a row that has no code. */
const personKey = (code: string | null | undefined, name: string | null | undefined): string | null =>
  (code ?? '').trim() || (name ?? '').trim() || null;
const managerOf = (j: NewJoiner) => ({ code: j.reportingManagerCode, name: j.reportingManagerName });
const tradeHeadOf = (j: NewJoiner) => ({ code: j.tradeHeadCode ?? null, name: j.tradeHeadName ?? null });

/** Whether a row passes the filters. Buddy says nothing about a plan that has ended,
 *  so it narrows Active rows only. */
const matchesFilters = (j: NewJoiner, f: JoinerFilters): boolean => {
  const am = managerOf(j);
  const th = tradeHeadOf(j);
  return (f.buddy.length === 0 || j.planStatus !== 'active' || f.buddy.includes(waitingForBuddy(j) ? 'waiting' : 'has'))
    && (f.plan.length === 0 || f.plan.includes(j.batchInitiated ? 'initiated' : 'not_initiated'))
    && (f.am.length === 0 || f.am.includes(personKey(am.code, am.name) ?? ''))
    && (f.th.length === 0 || f.th.includes(personKey(th.code, th.name) ?? ''));
};

/** The people one filter offers: those on the plan tab's rows, plus anyone already
 *  chosen (named from any loaded row). Name first, code after, as on the PIP page. */
const personOptions = (
  tabRows: NewJoiner[], allRows: NewJoiner[], of: (j: NewJoiner) => { code: string | null; name: string | null },
  selected: string[],
): FilterOption[] => {
  const labels = new Map<string, string>();
  const add = (j: NewJoiner) => {
    const p = of(j);
    const key = personKey(p.code, p.name);
    if (!key || labels.has(key)) return;
    const name = (p.name ?? '').trim();
    labels.set(key, name && key !== name ? `${name} (${key})` : key);
  };
  tabRows.forEach(add);
  for (const key of selected) {
    if (labels.has(key)) continue;
    const j = allRows.find((r) => personKey(of(r).code, of(r).name) === key);
    if (j) add(j); else labels.set(key, key);
  }
  return [...labels].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
};

// projectId is nullable to match the other dashboard tabs — DashboardPage renders
// them before a project is chosen.
export default function OnboardingTab({ projectId }: { projectId: number | null }) {
  const [joiners, setJoiners] = useState<NewJoiner[]>([]);
  const [assignments, setAssignments] = useState<ShadowAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Non-error outcomes the user still needs told about — chiefly "the mapping was
  // left in place on purpose".
  const [notice, setNotice] = useState<string | null>(null);
  // What the worklist shows is the address (?list=<slug>), read on every render: the
  // plan tab (Active = plan ongoing; Completed = plan ended recently, still
  // extendable), a score chip, or the Buddy filter on "Waiting for a buddy" alone.
  // So the home screen and a colleague's link land on the same rows.
  const { list: listSlug, openList, closeList } = useDashboardList();
  const view = useMemo(() => onboardingViewFromSlug(listSlug), [listSlug]);
  const planTab = view.plan;
  const scoreFilter: ScoreChipKey | null = view.score;
  const showView = useCallback((next: OnboardingView) => {
    // Plain Active is the dashboard's bare address, so taking a filter off takes the
    // slug off with it.
    if (isDefaultOnboardingView(next)) closeList();
    else openList(onboardingSlug(next));
  }, [openList, closeList]);
  // The Filters window's choices, kept across the plan tabs. On Active with no score
  // chip the address decides whether Buddy is "Waiting for a buddy" alone
  // (?list=no-buddy), read from it on every render; `heldFilters` holds the rest.
  const [heldFilters, setHeldFilters] = useState<JoinerFilters>(NO_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const addressHasBuddy = planTab === 'active' && scoreFilter === null;
  const filters = useMemo<JoinerFilters>(() => {
    if (!addressHasBuddy) return heldFilters;
    if (view.noBuddy) return { ...heldFilters, buddy: ['waiting'] };
    return isWaitingOnly(heldFilters.buddy) ? { ...heldFilters, buddy: [] } : heldFilters;
  }, [heldFilters, addressHasBuddy, view.noBuddy]);
  // Every move between plan tabs and score chips: the filters stay as they are, and
  // ?list=no-buddy comes back whenever plain Active shows that list.
  const go = useCallback((plan: OnboardingPlan, score: ScoreChipKey | null) => {
    setHeldFilters(filters);
    showView({ plan, score, noBuddy: plan === 'active' && score === null && isWaitingOnly(filters.buddy) });
  }, [filters, showView]);
  const changeFilters = useCallback((next: JoinerFilters) => {
    setHeldFilters(next);
    if (addressHasBuddy && isWaitingOnly(next.buddy) !== view.noBuddy) {
      showView({ plan: 'active', score: null, noBuddy: isWaitingOnly(next.buddy) });
    }
  }, [addressHasBuddy, view.noBuddy, showView]);
  const toggleFilter = (key: JoinerFilterKey, value: string) => {
    const cur = filters[key] as string[];
    changeFilters({ ...filters, [key]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value] });
  };
  const clearFilter = (key: JoinerFilterKey) => changeFilters({ ...filters, [key]: [] });
  const clearFilters = () => changeFilters(NO_FILTERS);
  // A link can open the worklist narrowed to one joiner (?employeeCode=).
  const [search, setSearch] = useLinkedSearch();
  const [assignFor, setAssignFor] = useState<NewJoiner | null>(null);
  const [extendFor, setExtendFor] = useState<NewJoiner | null>(null);
  const [historyFor, setHistoryFor] = useState<NewJoiner | null>(null);
  const [scoreFor, setScoreFor] = useState<NewJoiner | null>(null);
  const [downloading, setDownloading] = useState(false);
  // Onboarding scores for every loaded row (both plan tabs), keyed by lower-cased
  // employee code. Loaded after the rows, separately, so a scores failure only
  // blanks the score column — the worklist itself still works.
  const [scores, setScores] = useState<Map<string, JoinerScore>>(() => new Map());
  const [scoresStatus, setScoresStatus] = useState<ScoresStatus>('loading');
  // Only the latest scores request may land (a decision refetches while an older
  // load could still be in flight).
  const scoresReq = useRef(0);

  const loadScores = useCallback(async (list: NewJoiner[]) => {
    if (!projectId) return;
    const req = ++scoresReq.current;
    setScoresStatus('loading');
    try {
      const next = new Map<string, JoinerScore>();
      for (let i = 0; i < list.length; i += SCORE_CHUNK) {
        const res = await onboardingAPI.getScores({
          projectId, joiners: list.slice(i, i + SCORE_CHUNK).map(compliancePayload),
        });
        if (req !== scoresReq.current) return;
        for (const s of (res.data?.data ?? []) as JoinerScore[]) next.set(scoreKey(s.employeeCode), s);
      }
      if (req !== scoresReq.current) return;
      setScores(next);
      setScoresStatus('ready');
    } catch {
      if (req !== scoresReq.current) return;
      // Drop what was there: a stale pill after a decision would be worse than "—".
      setScores(new Map());
      setScoresStatus('error');
    }
  }, [projectId]);

  const fetchAll = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const [joinersRes, assignRes] = await Promise.all([
        // A generous fetch bound; the server tags each joiner active/completed from
        // their own plan length and drops any whose plan ended long ago, so this
        // window just needs to comfortably cover the plan + the recent-completed tail.
        onboardingAPI.getJoiners({ projectId, days: ONBOARDING_FETCH_DAYS }),
        onboardingAPI.getAssignments({ projectId }),
      ]);
      const nextJoiners: NewJoiner[] = joinersRes.data?.data ?? [];
      const nextAssignments: ShadowAssignment[] = assignRes.data?.data ?? [];
      setJoiners(nextJoiners);
      setAssignments(nextAssignments);
      setForbidden(null);
      // Scores follow every rows (re)load — and only that: search, sort and the
      // plan tab all work on what is already loaded. Not awaited, so the table
      // shows straight away with "…" in the score column.
      void loadScores(mergeJoinerRows(nextJoiners, nextAssignments));
    } catch (err) {
      if (httpStatus(err) === 403) {
        setForbidden(apiMessage(err, 'You do not have access to Onboarding for this project.'));
        return;
      }
      setForbidden(null);
      setError(apiMessage(err, 'Failed to load new joiners.'));
    } finally {
      setLoading(false);
    }
  }, [projectId, loadScores]);

  // Another project's scores (and a filter chosen for them) must not show against
  // this project's rows, nor may a request still in flight for them land. The filter
  // is dropped only when one project REPLACES another: the first project to arrive
  // is how the home screen and a link land on a filter.
  const shownProject = useRef(projectId);
  useEffect(() => {
    scoresReq.current += 1;
    setScores(new Map());
    setScoresStatus('loading');
    const replaced = shownProject.current != null && shownProject.current !== projectId;
    shownProject.current = projectId;
    if (replaced) setHeldFilters(NO_FILTERS);
    if (replaced && (view.score || view.noBuddy)) showView({ plan: view.plan, score: null, noBuddy: false });
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const rows = useMemo(() => mergeJoinerRows(joiners, assignments), [joiners, assignments]);

  const activeRows = useMemo(() => rows.filter((j) => j.planStatus === 'active'), [rows]);
  const completedRows = useMemo(() => rows.filter((j) => j.planStatus === 'completed'), [rows]);

  // Each plan tab's rows through the filters: the tab counts, the score chips, the
  // table and the download all start from these.
  const activeShown = useMemo(() => activeRows.filter((j) => matchesFilters(j, filters)), [activeRows, filters]);
  const completedShown = useMemo(() => completedRows.filter((j) => matchesFilters(j, filters)), [completedRows, filters]);
  const planRows = planTab === 'active' ? activeRows : completedRows;
  const tabRows = planTab === 'active' ? activeShown : completedShown;
  // Buddy is not offered on Completed (see matchesFilters), so there it is held, not on.
  const buddyHeld = planTab !== 'active' && filters.buddy.length > 0;
  const appliedCount = FILTER_KEYS.filter((k) => filters[k].length > 0 && !(k === 'buddy' && buddyHeld)).length;

  // How many of the CURRENT plan tab's joiners fall in each score chip. Like the
  // tab counts, these follow the filters and ignore the search box.
  const chipCounts = useMemo(() => {
    const counts: Record<ScoreChipKey, number> = { ready: 0, extend: 0, not_enough: 0, in_training: 0, decided: 0 };
    for (const j of tabRows) {
      const s = scores.get(scoreKey(j.employeeCode));
      if (!s) continue;
      for (const c of SCORE_CHIPS) if (c.match(s)) counts[c.key] += 1;
    }
    return counts;
  }, [tabRows, scores]);

  // A chip filter only stays on while its group still has someone in it — after a
  // plan-tab switch, or once a decision empties it — otherwise the table would be
  // filtered by a chip that is no longer shown. Held while scores reload.
  useEffect(() => {
    if (scoreFilter && scoresStatus !== 'loading' && chipCounts[scoreFilter] === 0) go(planTab, null);
  }, [scoreFilter, scoresStatus, chipCounts, planTab, go]);

  const filteredJoiners = useMemo(() => {
    const chip = scoreFilter ? SCORE_CHIPS.find((c) => c.key === scoreFilter) : undefined;
    const base = chip
      ? tabRows.filter((j) => {
        const s = scores.get(scoreKey(j.employeeCode));
        return !!s && chip.match(s);
      })
      : tabRows;
    const q = search.trim().toLowerCase();
    if (!q) return base;
    return base.filter(
      (j) =>
        j.employeeName.toLowerCase().includes(q) ||
        j.employeeCode.toLowerCase().includes(q) ||
        j.city.toLowerCase().includes(q) ||
        j.state.toLowerCase().includes(q),
    );
  }, [tabRows, scores, scoreFilter, search]);

  const sort = useTableSort<NewJoiner>(
    filteredJoiners,
    {
      name: (r) => r.employeeName,
      code: (r) => r.employeeCode,
      doj: (r) => r.dateOfJoining ?? '',
      days: (r) => r.daysSinceJoining ?? -1,
      location: (r) => `${r.city} ${r.state}`.trim(),
      // Reporting Manager = the name shown (a code-only row shows "—" → sinks).
      manager: (r) => r.reportingManagerName || null,
      status: (r) => (r.assignment ? 2 : r.isDeployed ? 1 : 0),
      // The % shown; a joiner with no score yet (or scores not loaded) sinks.
      score: (r) => scores.get(scoreKey(r.employeeCode))?.scorePct ?? null,
    },
    { defaultKey: 'days', defaultDir: 'asc' },
  );

  // The score modal stays open after a decision, and an Extend decision moves the
  // plan end — so hand it the refetched row's plan end, keyed on the value so an
  // unrelated reload doesn't give it a new joiner object.
  const scorePlanEnd = scoreFor
    ? (rows.find((r) => r.employeeCode === scoreFor.employeeCode) ?? scoreFor).planEndDate
    : null;
  const scoreJoiner = useMemo(
    () => (scoreFor
      ? {
        employeeCode: scoreFor.employeeCode,
        employeeName: scoreFor.employeeName,
        dateOfJoining: scoreFor.dateOfJoining,
        planEndDate: scorePlanEnd,
      }
      : null),
    [scoreFor, scorePlanEnd],
  );

  // A recorded decision leaves the modal open (it shows the history), says what
  // happened on the tab, and reloads rows + scores so the pill and the plan tab
  // (an Extend moves the joiner back to Active) catch up.
  const handleScoreDecided = ({ decision }: { decision: ScoreDecision; score: JoinerScore }) => {
    // Only a Completed joiner actually moves tabs; an Active one just runs longer.
    const movedBack = scoreFor?.planStatus === 'completed' ? ' The joiner is back in Active.' : '';
    setNotice(decision.decision === 'deploy'
      ? 'Decision recorded: Cleared for deployment.'
      : `Decision recorded: onboarding extended${decision.extendedTo ? ` to ${shortDate(decision.extendedTo)}` : ''}.${movedBack}`);
    void fetchAll();
  };

  // The Filters window. Options are the people and values on this plan tab's rows,
  // plus anything already chosen. Buddy is left out on Completed.
  const filterSections: FilterSection[] = [
    ...(planTab === 'active' ? [{
      kind: 'multiselect' as const, key: 'buddy', label: FILTER_LABEL.buddy, options: BUDDY_OPTIONS, allLabel: 'Any',
      selected: filters.buddy, onToggle: (v: string) => toggleFilter('buddy', v), onClear: () => clearFilter('buddy'),
    }] : []),
    {
      kind: 'multiselect', key: 'plan', label: FILTER_LABEL.plan, options: PLAN_OPTIONS, allLabel: 'Any',
      selected: filters.plan, onToggle: (v: string) => toggleFilter('plan', v), onClear: () => clearFilter('plan'),
    },
    {
      kind: 'multiselect', key: 'am', label: FILTER_LABEL.am, searchable: true,
      options: personOptions(planRows, rows, managerOf, filters.am),
      selected: filters.am, onToggle: (v: string) => toggleFilter('am', v), onClear: () => clearFilter('am'),
    },
    {
      kind: 'multiselect', key: 'th', label: FILTER_LABEL.th, searchable: true,
      options: personOptions(planRows, rows, tradeHeadOf, filters.th),
      selected: filters.th, onToggle: (v: string) => toggleFilter('th', v), onClear: () => clearFilter('th'),
    },
  ];

  // One removable chip per chosen value, under the toolbar. A person reads by name.
  const personName = (of: typeof managerOf, key: string): string => {
    const j = rows.find((r) => personKey(of(r).code, of(r).name) === key);
    return (j && (of(j).name ?? '').trim()) || key;
  };
  const filterChips = FILTER_KEYS.flatMap((key) => (filters[key] as string[]).map((value) => ({
    key,
    value,
    text: key === 'buddy' ? BUDDY_OPTIONS.find((o) => o.value === value)?.label ?? value
      : key === 'plan' ? PLAN_OPTIONS.find((o) => o.value === value)?.label ?? value
        : personName(key === 'am' ? managerOf : tradeHeadOf, value),
    held: key === 'buddy' && buddyHeld,
  })));

  // Day-wise export of exactly what's on screen (current tab + filters + search + score
  // chip, in the table's sort order): one row per joiner per plan day, with the checklist
  // and Sr iPro sign-off status for that day. One API call covers every joiner.
  // The joiner-level onboarding score columns come from the scores already loaded
  // for the table (blank if they didn't load), not a second fetch.
  const handleDownload = async () => {
    if (!projectId || downloading) return;
    const visible = sort.sorted;
    if (visible.length === 0) return;
    setDownloading(true);
    setActionError(null);
    try {
      const res = await onboardingAPI.getCompliance({ projectId, joiners: visible.map(compliancePayload) });
      const byCode = new Map<string, JoinerCompliance>(
        ((res.data?.data ?? []) as JoinerCompliance[]).map((c) => [c.employeeCode.trim().toLowerCase(), c]),
      );
      const headers = [
        'Employee Code', 'Employee Name', 'Designation', 'Date of Joining', 'City', 'State',
        'Reporting Manager', 'Plan Status', 'Plan End Date',
        'Onboarding score %', 'Score advice', 'Score decision',
        'Day', 'Date', 'Planned Activities',
        'Shadow Training Day', 'Onboarding Checklist', 'Checklist Submitted At',
        'Sr iPro Sign-off', 'Sign-off Buddy', 'Sign-off Submitted At',
      ];
      const rows: string[][] = [];
      for (const j of visible) {
        const planStatus = !j.batchInitiated ? 'Not initiated' : j.planStatus === 'completed' ? 'Completed' : 'Active';
        const sc = scores.get(scoreKey(j.employeeCode));
        const base = [
          j.employeeCode, j.employeeName, j.designation || '', j.dateOfJoining || '', j.city || '', j.state || '',
          j.reportingManagerName || '', planStatus, j.planEndDate || '',
          sc?.scorePct != null ? String(Math.round(sc.scorePct * 10) / 10) : '',
          sc ? scoreAdviceText(sc) : '',
          scoreDecisionText(sc?.latestDecision ?? null),
        ];
        const c = byCode.get(j.employeeCode.trim().toLowerCase());
        if (!c || c.days.length === 0) {
          rows.push([...base, '', '', 'No joining date on record', '', '', '', '', '', '']);
          continue;
        }
        for (const d of c.days) {
          rows.push([
            ...base,
            `Day ${d.dayNumber + 1}`, d.date, d.activities.join(' | '), d.isShadowDay ? 'Yes' : 'No',
            COMPLIANCE_META[d.checklist.status].label, fmtSubmitted(d.checklist.submittedAt),
            COMPLIANCE_META[d.signoff.status].label, d.signoff.buddyName || '', fmtSubmitted(d.signoff.submittedAt),
          ]);
        }
      }
      downloadCsv(`onboarding-checklist-signoff-${planTab}-${todayIst()}.csv`, buildCsv(headers, rows));
    } catch (err) {
      setActionError(apiMessage(err, 'Could not prepare the download. Please try again.'));
    } finally {
      setDownloading(false);
    }
  };
  if (forbidden) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-6 text-sm text-amber-900">
        <div className="mb-1 flex items-center gap-2 font-semibold">
          <AlertTriangle className="h-4 w-4" />
          Access needed
        </div>
        {forbidden}
      </div>
    );
  }

  const thCls = 'whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-500';

  return (
    <div className="space-y-4">
      {/* Controls: the worklist is now framed by the onboarding-plan lifecycle —
          an Active tab (plan ongoing) and a Completed tab (plan just ended, still
          extendable). No "joined within N days" filter: the plan length decides
          who is onboarding. Each row opens the plan + buddy modal; Completed rows
          also carry an Extend action. */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-1 rounded-lg border border-gray-200 bg-gray-100 p-1">
            <button
              type="button"
              onClick={() => go('active', scoreFilter)}
              className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                planTab === 'active' ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Active ({activeShown.length})
            </button>
            <button
              type="button"
              onClick={() => go('completed', scoreFilter)}
              className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                planTab === 'completed' ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Completed ({completedShown.length})
            </button>
          </div>
          <div className="flex items-center gap-2">
            {/* The same Filters button as the other dashboards, at the toolbar's size. */}
            <button
              type="button"
              onClick={() => setFiltersOpen(true)}
              title="Open filters"
              className={`inline-flex flex-shrink-0 items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                appliedCount > 0
                  ? 'border-primary-200 bg-primary-50 text-primary-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" /> Filters
              {appliedCount > 0 && (
                <span className="ml-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary-600 px-1 text-[10px] font-bold text-white">
                  {appliedCount}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading || sort.sorted.length === 0}
              title="Download the joiners shown here with their day-by-day checklist and sign-off status"
              className="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-primary-200 bg-white px-3 py-1.5 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" /> {downloading ? 'Preparing…' : 'Download'}
            </button>
            <div className="w-64">
              <SearchBox value={search} onChange={setSearch} placeholder="Search joiners..." />
            </div>
          </div>
        </div>

        {/* The filters that are on, one removable chip each. Drawn only when there is
            one, so the page does not move for an empty line. Buddy on Completed is held
            for Active, and says so. */}
        {filterChips.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filters on">
            {filterChips.map((c) => (
              <span
                key={`${c.key}:${c.value}`}
                title={c.held ? 'Applies on the Active tab only' : undefined}
                className={`inline-flex h-7 items-center gap-1 rounded-full border pl-3 pr-1 text-xs ${
                  c.held
                    ? 'border-dashed border-gray-300 bg-white text-gray-500'
                    : 'border-primary-200 bg-primary-50 text-primary-800'
                }`}
              >
                <span className={c.held ? 'text-gray-400' : 'text-primary-600'}>{FILTER_LABEL[c.key]}:</span>
                <span className="max-w-[14rem] truncate font-semibold">{c.text}</span>
                {c.held && <span className="text-gray-400">· Active only</span>}
                <button
                  type="button"
                  onClick={() => toggleFilter(c.key, c.value)}
                  aria-label={`Remove filter ${FILTER_LABEL[c.key]}: ${c.text}`}
                  className={`ml-0.5 rounded-full p-0.5 ${c.held ? 'hover:bg-gray-100' : 'hover:bg-primary-100'}`}
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </span>
            ))}
            <button
              type="button"
              onClick={clearFilters}
              className="rounded px-1.5 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {planTab === 'completed' && (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-600">
          <CalendarDays className="h-4 w-4 shrink-0 text-gray-400" />
          These joiners have finished their onboarding plan. Extend one to give them more time — they move back to Active.
        </div>
      )}

      {notice && (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            {notice}
          </span>
          <button
            type="button"
            onClick={() => setNotice(null)}
            className="shrink-0 rounded p-0.5 text-amber-600 hover:bg-amber-100 hover:text-amber-800"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {actionError && (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{actionError}</span>
          <button
            type="button"
            onClick={() => setActionError(null)}
            className="shrink-0 rounded p-0.5 text-red-500 hover:bg-red-100 hover:text-red-700"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Onboarding score groups for this plan tab. Each chip filters the table to
          its joiners (click again to clear); empty groups aren't shown. */}
      {!loading && !error && SCORE_CHIPS.some((c) => chipCounts[c.key] > 0) && (
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by onboarding score">
          <span className="text-xs font-medium text-gray-500">Onboarding score</span>
          {SCORE_CHIPS.filter((c) => chipCounts[c.key] > 0).map((c) => {
            const on = scoreFilter === c.key;
            return (
              <button
                key={c.key}
                type="button"
                aria-pressed={on}
                onClick={() => go(planTab, on ? null : c.key)}
                title={c.title}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  on
                    ? 'border-primary-300 bg-primary-50 text-primary-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span className={`h-2 w-2 rounded-full ${c.dot}`} aria-hidden="true" />
                {c.label}
                <span className="font-semibold text-gray-900">{chipCounts[c.key]}</span>
              </button>
            );
          })}
        </div>
      )}

      {!loading && !error && scoresStatus === 'error' && (
        <div className="text-xs text-gray-500">
          Onboarding scores couldn’t load.{' '}
          <button
            type="button"
            onClick={() => void loadScores(rows)}
            className="font-medium text-primary-600 hover:underline"
          >
            Try again
          </button>
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50/70 text-left">
              <tr>
                <SortTh label="Employee" sortKey="name" sort={sort} className={thCls} />
                <SortTh label="Joined" sortKey="doj" sort={sort} className={thCls} />
                <SortTh label="Location" sortKey="location" sort={sort} className={thCls} />
                <SortTh label="Reporting Manager" sortKey="manager" sort={sort} className={thCls} />
                <SortTh label="Status" sortKey="status" sort={sort} className={thCls} />
                <SortTh
                  label="Onboarding score"
                  sortKey="score"
                  sort={sort}
                  className={thCls}
                  title="Mean of the buddy's daily sign-off ratings, each day weighing the same. Click a score for the day-by-day breakdown and to record a decision."
                />
                <th className={`${thCls} text-center`}>Plan &amp; buddy</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400"><InlineLoader label="Loading…" className="flex justify-center" /></td></tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center">
                    <p className="text-sm text-red-500">{error}</p>
                    <button onClick={fetchAll} className="mt-2 text-sm text-blue-600 hover:underline">Try again</button>
                  </td>
                </tr>
              ) : sort.sorted.length === 0 && appliedCount > 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center">
                    <p className="text-sm text-gray-500">No joiner matches these filters.</p>
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="mt-3 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      Clear filters
                    </button>
                  </td>
                </tr>
              ) : sort.sorted.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">
                    {search.trim()
                      ? 'No joiner matches your search.'
                      : scoreFilter
                          ? 'No joiner in this onboarding score group.'
                          : planTab === 'active'
                            ? 'No one is currently in their onboarding plan.'
                            : 'No one has completed their onboarding plan recently.'}
                  </td>
                </tr>
              ) : (
                sort.sorted.map((j) => {
                  const a = j.assignment;
                  return (
                    // ONE way in: the whole row opens the onboarding modal — the
                    // full plan plus the buddy (auto-assigned, editable). No inline
                    // panel and no separate assign button; the modal does it all.
                    <tr
                      key={j.employeeCode}
                      className={`transition-colors ${j.batchInitiated ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                      onClick={j.batchInitiated ? () => setAssignFor(j) : undefined}
                    >
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-gray-900">{j.employeeName}</span>
                          <EmployeeTags designation={j.designation} />
                        </div>
                        <div className="text-xs text-gray-500">{j.employeeCode}</div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="text-gray-900">{fmtDate(j.dateOfJoining)}</div>
                        <div className="text-xs text-gray-500">
                          {j.daysSinceJoining == null
                            ? '—'
                            : j.daysSinceJoining === 0
                              ? 'Today'
                              : j.daysSinceJoining === 1
                                ? 'Yesterday'
                                : `${j.daysSinceJoining} days ago`}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="text-gray-900">{j.city || '—'}</div>
                        <div className="text-xs text-gray-500">{j.state || 'State unknown'}</div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {j.reportingManagerName || j.reportingManagerCode ? (
                          <>
                            <div className="text-gray-900">{j.reportingManagerName || '—'}</div>
                            {!!j.reportingManagerCode && <div className="text-xs text-gray-500">{j.reportingManagerCode}</div>}
                          </>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {!j.batchInitiated ? (
                          // Nothing runs for this joiner until the batch is
                          // initiated from the Journey page — say so plainly, and
                          // (below) give the row no actions.
                          <span
                            className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700"
                            title="Initiate the onboarding batch from Grow → Onboarding Setup → Journey to start this joiner's plan"
                          >
                            <AlertTriangle className="h-3.5 w-3.5" /> Onboarding Plan not initiated
                          </span>
                        ) : (
                          // Plan is running — no "initiated" tag (the row actions
                          // signal that). Instead show WHICH onboarding day the
                          // joiner is on today and what the plan schedules for it:
                          // shadow training and/or the day's session(s).
                          <>
                          {j.todayActivity ? (
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="inline-flex rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-semibold text-primary-700">
                                Day {j.todayActivity.dayNumber + 1}
                              </span>
                              {j.todayActivity.isShadow && (
                                <span className="inline-flex rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-medium text-violet-700">
                                  Shadow training
                                </span>
                              )}
                              {j.todayActivity.sessionTitles.slice(0, 2).map((t, i) => (
                                <span key={i} className="inline-flex max-w-[10rem] truncate rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700" title={t}>
                                  {t}
                                </span>
                              ))}
                              {j.todayActivity.sessionTitles.length > 2 && (
                                <span
                                  className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
                                  title={j.todayActivity.sessionTitles.slice(2).join(', ')}
                                >
                                  +{j.todayActivity.sessionTitles.length - 2}
                                </span>
                              )}
                              {!j.todayActivity.isShadow && j.todayActivity.sessionTitles.length === 0 && (
                                <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">
                                  No session today
                                </span>
                              )}
                            </div>
                          ) : (
                            // Today falls outside the joiner's plan window.
                            <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">
                              {j.planStatus === 'completed' ? 'Plan complete' : 'Onboarding'}
                            </span>
                          )}
                          {/* On a shadow day, name the buddy the joiner is training with. */}
                          {j.todayActivity?.isShadow && a && (
                            <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                              with {a.buddyName || a.buddyCode}
                              {a.assignmentSource === 'auto' && (
                                <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700" title="Auto-assigned by city match">
                                  <Sparkles className="h-3 w-3" /> Auto
                                </span>
                              )}
                            </div>
                          )}
                          {j.planEndDate && (
                            <div className="mt-1 flex items-center gap-1 text-[11px] text-gray-400">
                              {j.planStatus === 'completed'
                                ? `Plan ended ${fmtDate(j.planEndDate)}`
                                : `Onboarding to ${fmtDate(j.planEndDate)}`}
                              {j.extendedTo && (
                                <span className="rounded bg-blue-50 px-1 text-[10px] font-semibold text-blue-700" title={`Extended to ${fmtDate(j.extendedTo)}`}>extended</span>
                              )}
                            </div>
                          )}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <ScoreCell
                          joiner={j}
                          score={scores.get(scoreKey(j.employeeCode))}
                          status={scoresStatus}
                          onOpen={() => setScoreFor(j)}
                        />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {/* Actions appear ONLY once the batch is initiated — before
                            that, the joiner has no running plan, so there is nothing
                            to view, assign or extend from here. The entry point is
                            "Initiate Batch" on the Journey page. */}
                        {!j.batchInitiated ? (
                          <div className="text-center text-xs text-gray-300">—</div>
                        ) : (
                          <div className="flex items-center justify-center gap-1">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setAssignFor(j); }}
                              title={j.planStatus === 'completed' ? 'View onboarding record' : a ? 'View plan & buddy' : 'Assign a buddy'}
                              aria-label={j.planStatus === 'completed' ? 'View onboarding record' : a ? 'View plan and buddy' : 'Assign a buddy'}
                              className="rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-primary-600"
                            >
                              {j.planStatus === 'completed' || a ? <Eye className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setExtendFor(j); }}
                              title="Extend onboarding"
                              aria-label="Extend onboarding"
                              className="rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-primary-600"
                            >
                              <CalendarPlus className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setHistoryFor(j); }}
                              title="Checklist & sign-off history"
                              aria-label="Checklist and sign-off history"
                              className="rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-primary-600"
                            >
                              <ListChecks className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* The single onboarding modal — full plan + the buddy (auto-assigned when
          eligible, editable). Opened from any row; drives BOTH create and edit off
          the row's own assignment, so there is no separate edit path. Rows already
          carry the joiner's area + DOJ (rebuilt from the assignment for anyone who
          aged out of the joiner window), so the modal always has what it needs. */}
      {assignFor && projectId != null && (() => {
        const modalAsg = assignFor.assignment ?? null;
        return (
          <AssignBuddyModal
            projectId={projectId}
            joiner={assignFor}
            editing={modalAsg}
            onClose={() => setAssignFor(null)}
            onSaved={(notice) => {
              setAssignFor(null);
              if (notice) setNotice(notice);
              void fetchAll();
            }}
          />
        );
      })()}

      {extendFor && projectId != null && (
        <ExtendPlanModal
          projectId={projectId}
          joiner={extendFor}
          onClose={() => setExtendFor(null)}
          onSaved={() => {
            setExtendFor(null);
            setNotice('Onboarding plan extended — the joiner is back in Active.');
            void fetchAll();
          }}
        />
      )}

      {historyFor && projectId != null && (
        <ComplianceHistoryModal projectId={projectId} joiner={historyFor} onClose={() => setHistoryFor(null)} />
      )}

      <DashboardFilterModal
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="Filters — Onboarding"
        sections={filterSections}
        activeCount={appliedCount}
        onClearAll={clearFilters}
      />

      {scoreJoiner && projectId != null && (
        <OnboardingScoreModal
          projectId={projectId}
          joiner={scoreJoiner}
          onClose={() => setScoreFor(null)}
          onDecided={handleScoreDecided}
        />
      )}
    </div>
  );
}
